import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, LoaderCircle, Mic, X } from 'lucide-react';
import { ImagePurposeAttachMenu } from '../../components/shared/ImagePurposeAttachMenu';
import { useI18n } from '../../hooks/useI18n';
import { useSpeechInput } from '../../hooks/useSpeechInput';
import { classNames } from '../../utils/classNames';
import {
  listImageFiles,
  REFERENCE_IMAGE_MAX_COUNT,
  REFERENCE_IMAGE_MAX_SIZE_MB,
  validateReferenceImage
} from '../../utils/referenceImages';

export const ProjectPromptComposer = ({
  value,
  onChange,
  attachments = [],
  onAttachmentsChange,
  loading = false,
  disabled = false,
  onSubmit,
  inputRef,
  suggestions = []
}) => {
  const { t, language } = useI18n();
  const textareaRef = useRef(null);
  const dictationBaseRef = useRef('');
  const [voiceError, setVoiceError] = useState('');
  const [attachError, setAttachError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const canSubmit = Boolean(value.trim()) || attachments.length > 0;
  const canAddAttachments = attachments.length < REFERENCE_IMAGE_MAX_COUNT;
  const attachLimitMessage = t('aiReferenceImagesMax', { max: REFERENCE_IMAGE_MAX_COUNT });

  const setInputRef = useCallback(
    (node) => {
      textareaRef.current = node;
      if (!inputRef) {
        return;
      }
      if (typeof inputRef === 'function') {
        inputRef(node);
        return;
      }
      inputRef.current = node;
    },
    [inputRef]
  );

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 40), 128)}px`;
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [resizeTextarea, value]);

  const speechInput = useSpeechInput({
    language: language === 'fa' ? 'fa-IR' : 'en-US',
    onResult: (transcript) => {
      if (!transcript) {
        return;
      }
      onChange(`${dictationBaseRef.current}${transcript}`);
    },
    onError: (code) => {
      if (code === 'unsupported') {
        setVoiceError(t('chatVoiceUnsupported'));
        return;
      }
      if (code !== 'aborted' && code !== 'no-speech') {
        setVoiceError(t('chatVoiceError'));
      }
    }
  });

  const addAttachments = (fileList, purpose = 'attach') => {
    setAttachError('');
    const files = listImageFiles(fileList);
    if (!files.length) {
      return;
    }

    const remainingSlots = REFERENCE_IMAGE_MAX_COUNT - attachments.length;
    if (remainingSlots <= 0) {
      setAttachError(attachLimitMessage);
      return;
    }

    const next = [...attachments];
    let errorKey = null;

    files.slice(0, remainingSlots).forEach((file) => {
      const validation = validateReferenceImage(file);
      if (!validation.ok) {
        errorKey = validation.errorKey;
        return;
      }

      next.push({ file, purpose, url: URL.createObjectURL(file) });
    });

    if (next.length !== attachments.length) {
      onAttachmentsChange?.(next);
    }

    if (files.length > remainingSlots && !errorKey) {
      setAttachError(attachLimitMessage);
      return;
    }

    if (errorKey) {
      setAttachError(t(errorKey));
    }
  };

  const removeAttachment = (index) => {
    const next = attachments.filter((_, itemIndex) => itemIndex !== index);
    const removed = attachments[index];
    if (removed?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(removed.url);
    }
    onAttachmentsChange?.(next);
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    if (loading || disabled || !canSubmit) {
      return;
    }
    speechInput.stop();
    onSubmit?.(event);
  };

  const toggleVoice = () => {
    setVoiceError('');
    if (speechInput.listening) {
      speechInput.stop();
      return;
    }
    if (!speechInput.supported) {
      setVoiceError(t('chatVoiceUnsupported'));
      return;
    }
    dictationBaseRef.current = value.trim() ? `${value.trim()} ` : '';
    speechInput.start();
  };

  const handleDragOver = (event) => {
    if (disabled || loading || !event.dataTransfer?.types?.includes('Files')) {
      return;
    }
    event.preventDefault();
    if (canAddAttachments) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (disabled || loading) {
      return;
    }
    addAttachments(event.dataTransfer?.files, 'attach');
  };

  const handlePaste = (event) => {
    const files = listImageFiles(event.clipboardData?.files).filter((file) => file.type.startsWith('image/'));
    if (!files.length) {
      return;
    }
    event.preventDefault();
    addAttachments(files, 'attach');
  };

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      {attachments.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2 px-1">
          {attachments.map((attachment, index) => (
            <div key={attachment.url || index} className="relative">
              <img
                src={attachment.url}
                alt={t('chatAttachmentPreviewAlt')}
                className="h-14 w-14 rounded-xl border border-neutral-200 object-cover dark:border-neutral-800"
              />
              {attachment.purpose === 'design' ? (
                <span className="absolute inset-x-1 bottom-1 rounded bg-black/70 py-0.5 text-center text-[9px] font-medium text-white">
                  UI
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => removeAttachment(index)}
                aria-label={t('removeAttachment')}
                className="absolute -end-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-900/80 text-white transition hover:bg-rose-500"
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div
        className={classNames(
          'overflow-visible rounded-2xl border bg-white shadow-sm transition dark:bg-panel-strong',
          dragOver ? 'border-accent/50 ring-2 ring-accent/15' : 'border-border'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <label className="sr-only" htmlFor="project-prompt">
          {t('projectsComposerPlaceholder')}
        </label>
        <div className="flex items-end gap-1 px-2 py-2">
          <ImagePurposeAttachMenu
            disabled={loading || disabled || !canAddAttachments}
            onPick={addAttachments}
          />
          <textarea
            id="project-prompt"
            ref={setInputRef}
            value={value}
            disabled={loading || disabled}
            onChange={(event) => onChange(event.target.value)}
            onPaste={handlePaste}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                handleSubmit(event);
              }
            }}
            placeholder={t('projectsComposerPlaceholder')}
            rows={1}
            className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent py-2.5 text-base text-text outline-none placeholder:text-muted disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
          />
          <button
            type="button"
            disabled={loading || disabled}
            onClick={toggleVoice}
            title={t('chatVoiceInput')}
            aria-label={t('chatVoiceInput')}
            className={classNames(
              'mb-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full transition disabled:opacity-40',
              speechInput.listening
                ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-950'
                : 'text-neutral-500 hover:bg-neutral-200/70 hover:text-neutral-800 dark:hover:bg-neutral-800'
            )}
          >
            <Mic size={16} />
          </button>
          <button
            type="submit"
            disabled={!canSubmit || loading || disabled}
            aria-label={t('send')}
            className="mb-0.5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-950 text-white transition enabled:hover:bg-neutral-800 disabled:opacity-35 dark:bg-white dark:text-neutral-950"
          >
            {loading ? <LoaderCircle size={16} className="animate-spin" /> : <ArrowUp size={16} />}
          </button>
        </div>
      </div>

      <p className="mt-2 px-1 text-center text-[11px] leading-4 text-neutral-400">
        {t('composerAttachHint')} · {t('aiReferenceImagesConstraints', {
          max: REFERENCE_IMAGE_MAX_COUNT,
          sizeMb: REFERENCE_IMAGE_MAX_SIZE_MB
        })}
      </p>

      {suggestions.length > 0 ? (
        <div className="scrollbar-thin mt-3 flex gap-2 overflow-x-auto pb-0.5">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              disabled={loading || disabled}
              onClick={() => onChange(suggestion)}
              className="inline-flex shrink-0 whitespace-nowrap rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs text-neutral-500 transition hover:border-neutral-300 hover:text-neutral-800 disabled:opacity-50 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700"
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}

      {voiceError || attachError ? (
        <p className="mt-2 text-center text-xs text-rose-500">{voiceError || attachError}</p>
      ) : null}
    </form>
  );
};
